import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api, ApiError } from '@/api'
import { maskEmail } from '@/lib/format'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/contexts/ToastContext'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { TextField } from '@/components/ui/Field'
import { KeyDotsIcon, MailIcon } from '@/components/ui/icons'

/** Aba "Segurança": troca de e-mail e de senha, cada uma em sua própria linha. */
export function SecurityPage() {
  const { user } = useAuth()
  const [openSection, setOpenSection] = useState<'email' | 'password' | null>(null)

  return (
    <Card>
      <h2 className="px-5 py-4 text-[17px] font-semibold text-ink lg:text-[19px]">Segurança</h2>

      <SecurityRow
        icon={MailIcon}
        label={user?.email ? maskEmail(user.email) : 'E-mail'}
        open={openSection === 'email'}
        onToggle={() => setOpenSection((s) => (s === 'email' ? null : 'email'))}
      >
        <ChangeEmailForm onDone={() => setOpenSection(null)} />
      </SecurityRow>

      <SecurityRow
        icon={KeyDotsIcon}
        label="Senha"
        open={openSection === 'password'}
        onToggle={() => setOpenSection((s) => (s === 'password' ? null : 'password'))}
      >
        <ChangePasswordForm onDone={() => setOpenSection(null)} />
      </SecurityRow>
    </Card>
  )
}

/* ------------------------------------------------------------------ */

interface SecurityRowProps {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  label: string
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}

function SecurityRow({ icon: Icon, label, open, onToggle, children }: SecurityRowProps) {
  return (
    <div className="border-t border-line px-5 py-4">
      <div className="flex items-center justify-between gap-4">
        <span className="flex min-w-0 items-center gap-2.5">
          <Icon className="size-[18px] shrink-0 text-ink-soft" />
          <span className="truncate text-[15px] text-ink">{label}</span>
        </span>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="shrink-0 text-[13px] text-ink-soft transition-colors hover:text-brand hover:underline"
        >
          {open ? 'Cancelar' : 'Alterar'}
        </button>
      </div>

      {open && <div className="mt-4 animate-[slide-down_0.15s_ease-out]">{children}</div>}
    </div>
  )
}

/* ------------------------------------------------------------------ */

function ChangeEmailForm({ onDone }: { onDone: () => void }) {
  const { notify } = useToast()
  const [newEmail, setNewEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => api.auth.changeEmail({ newEmail }),
    onSuccess: () => {
      notify('Enviamos um link de confirmação para o novo e-mail.', 'success')
      setNewEmail('')
      onDone()
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? err.message : 'Não foi possível alterar o e-mail.',
      )
    },
  })

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    mutation.mutate()
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2" noValidate>
      <div className="flex flex-wrap items-end gap-3">
        <TextField
          type="email"
          required
          autoComplete="email"
          className="min-w-[240px] flex-1"
          value={newEmail}
          onChange={(event) => setNewEmail(event.target.value)}
          error={error}
        />
        <Button
          type="submit"
          loading={mutation.isPending}
          disabled={!newEmail}
          className="h-11 shrink-0"
        >
          Alterar email
        </Button>
      </div>

      <p className="text-xs leading-relaxed text-ink-faint">
        Enviaremos uma mensagem de confirmação para o novo e-mail informado. Seu e-mail só
        será alterado quando você clicar no link de confirmação que enviaremos para o novo
        e-mail.
      </p>
    </form>
  )
}

/* ------------------------------------------------------------------ */

function ChangePasswordForm({ onDone }: { onDone: () => void }) {
  const { notify } = useToast()
  const [form, setForm] = useState({ next: '', repeat: '', current: '' })
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () =>
      api.auth.changePassword({
        currentPassword: form.current,
        newPassword: form.next,
      }),
    onSuccess: () => {
      notify('Senha alterada com sucesso.', 'success')
      setForm({ next: '', repeat: '', current: '' })
      onDone()
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? err.message : 'Não foi possível alterar a senha.',
      )
    },
  })

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    if (form.next !== form.repeat) {
      setError('As senhas não são iguais.')
      return
    }
    mutation.mutate()
  }

  const filled = form.next && form.repeat && form.current

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2" noValidate>
      <div className="flex flex-wrap items-end gap-3">
        <TextField
          label="Nova senha"
          type="password"
          autoComplete="new-password"
          required
          minLength={6}
          className="min-w-[140px] flex-1"
          value={form.next}
          onChange={(event) => setForm((f) => ({ ...f, next: event.target.value }))}
        />
        <TextField
          label="Repita a nova senha"
          type="password"
          autoComplete="new-password"
          required
          className="min-w-[140px] flex-1"
          value={form.repeat}
          onChange={(event) => setForm((f) => ({ ...f, repeat: event.target.value }))}
        />
        <TextField
          label="Senha atual"
          type="password"
          autoComplete="current-password"
          required
          className="min-w-[140px] flex-1"
          value={form.current}
          onChange={(event) => setForm((f) => ({ ...f, current: event.target.value }))}
        />
        <Button
          type="submit"
          loading={mutation.isPending}
          disabled={!filled}
          className="h-11 shrink-0"
        >
          Alterar senha
        </Button>
      </div>

      {error && <p className="text-[13px] font-medium text-danger">{error}</p>}
    </form>
  )
}
